import { getSafeSearchParam } from "@mohalla-tech/xss-safeguard";
import constants from "../constants/index.js";
import { protectedRoutes, publicRoutes } from "../constants/routes.js";
import { getAppData, getOsName, isAndroid, isIos } from "./helpers/utils.js";

const { SHARE_LINK_WHATSAPP, DEFAULT_AUTH_TOKEN } = constants;
/**
 * number to the nearest rounded number.
 * eg. __getRounded(2.44) -> 2.4  and __getRounded(2.45) -> 2.5
 * @param {Number} n
 * @returns
 */
const __getRounded = (n) => Math.round(n * 10) / 10;

/**
 * If number is integer, changes to floor value,
 * if not then changes to rounded value and appends m or k according to value
 * @param {Number} num
 * @returns {Number}
 */
export const _normalizeNumber = (num, isInt) => {
  if (Number.isNaN(parseInt(num))) return 0;
  const useFunc = isInt ? Math.floor : __getRounded;
  if (num >= 1000000000) return `${useFunc(num / 1000000000)}B`;
  if (num >= 1000000) return `${useFunc(num / 1000000)}M`;
  if (num >= 1000) return `${useFunc(num / 1000)}K`;
  return num;
};
/**
 *
 * @param {string} str
 * @returns {string}
 */
export const _normalizeNumberInString = (str) =>
  str.replace(new RegExp(/([1-9][0-9]*)/, "g"), (x) => _normalizeNumber(x));
/**
 *
 * @returns {Boolean}
 */
export const isBrowser = () =>
  typeof window !== "undefined" &&
  !!window.document &&
  !!window.document.createElement;

/**
 *
 * @returns {Boolean}
 */
export const isProtectedRoute = (route) => {
  return protectedRoutes?.some((protectedRoute) =>
    route?.startsWith(protectedRoute)
  );
};

export const isPublicRoute = (route) => {
  return publicRoutes.some((publicRoute) => route.startsWith(publicRoute));
};

export const isHomeRoute = () => window?.location?.pathname === "/";

/**
 * The Intersection Observer API provides a way to asynchronously observe changes in the intersection of a target element with an ancestor element or with a top-level document's viewport.
 * It return whether IntersectionObserver is supported or not
 * @returns {Boolean}
 */
export const supportsIntersectionObserver = () =>
  isBrowser() && "IntersectionObserver" in window;

/**
 *Checks if runnning on localhost
 * @param {string} href
 * @returns {boolean}
 */
export const isLocalHost = (href) =>
  (href || window.location.href).includes("http://localhost");

/**
 *
 * @returns {string}
 */
export const nUserAgent = () =>
  navigator.userAgent || navigator.vendor || window.opera;

/**
 *Returns true if Prod environment
 * @returns {boolean}
 */
export const isProdEnv = () => true;

/**
 *
 * @param {string} url
 * @param {boolean} openInNewTab
 * @returns
 */
export function openExternalLink(url, openInNewTab) {
  const link = document.createElement("a");
  if (openInNewTab) {
    link.target = "_blank";
  }

  link.rel = "noopener noreferrer";
  link.href = url;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  return link;
}

/**
 * Function to return sharing link for whatsapp along with the text given
 * @param {string} link
 * @param {string} text
 * @returns {string}
 */
export function createShareOnWhatsAppLink(link, text = "") {
  const sharingLink = `${SHARE_LINK_WHATSAPP}${window.encodeURIComponent(
    `${text}
`
  )}${window.encodeURIComponent(link)}`;
  return sharingLink;
}

/**
 *Function to open external link of whatsapp for sharing
 * @param {string} link
 * @param {string} text
 * @returns {string}
 */
export function shareOnWhatsApp(link, text) {
  return openExternalLink(createShareOnWhatsAppLink(link, text), true);
}

/**
 *
 * @param {date} o
 * @returns
 */
export const date_uploaded = (o) => {
  try {
    return o ? new Date(parseInt(o) * 1000).toISOString() : null;
  } catch (e) {
    return null;
  }
};

export const appendWidthParam = (src, width) => {
  if (!src) return "";
  const HOST = "https://cdn-im";
  if (!src || !src.startsWith(HOST)) {
    return src;
  }
  const [url, query] = src.split("?");
  const queryParams = query ? query + `&w=${width}` : `w=${width}`;
  return `${url}?${queryParams}`;
};

/**
 * Function to reset profile pic if deleted
 * @param {string} src
 * @returns {string}
 */
export const resetIfDeletedPic = (src) =>
  src === "profile_delete" ? "" : appendWidthParam(src, "60");

/**
 *
 * @param {date} timeStamp
 * @param {string} format
 * @param {boolean} isString
 * @returns {string}
 */
export const getFormattedDate = (
  timeStamp,
  format = "$3-$2-$1",
  isString = false
) => {
  const date = new Date(isString ? timeStamp : timeStamp * 1000)
    .toLocaleString("en-GB")
    .split(",")[0];
  return date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, format);
};
/**
 *
 * @param {string} platform
 * @returns {object}
 */
export const getUserAuth = (pltForm = "android") => {
  const platform = pltForm || getOsName(navigator.userAgent);
  if (platform === "android") {
    const androidBridge = window.Android || window.ANDROID;
    return androidBridge?.get("userInfo");
  }
  return null;
};

/**
 * Function to give the time elapsed between given date and current date
 * @param {date} date
 * @param {boolean} short
 * @returns {string}
 */
export const timeSince = (date, short = false) => {
  if (date === "") {
    return false;
  }
  const diff = Math.floor((new Date() - date) / 1000);
  const seconds = diff < 0 ? 0 : diff;

  let interval = seconds / 31536000;

  if (interval > 1) {
    return `${Math.floor(interval)}${short ? "yr" : " years ago"}`;
  }
  interval = seconds / 2592000;
  if (interval > 1) {
    return `${Math.floor(interval)}${short ? "mo" : " months ago"}`;
  }
  interval = seconds / 86400;
  if (interval > 1) {
    return `${Math.floor(interval)}${short ? "d" : " days ago"}`;
  }
  interval = seconds / 3600;
  if (interval > 1) {
    return `${Math.floor(interval)}${short ? "hr" : " hours ago"}`;
  }
  interval = seconds / 60;
  if (interval > 1) {
    return `${Math.floor(interval)}${short ? "min" : " minutes ago"}`;
  }
  return `${Math.floor(seconds)}${short ? "s" : " seconds ago"}`;
};

/**
 * Function to change dd-mm-yy format of date to dd mmm, yyyy
 * @param {string} val
 * @returns {string}
 */
export const getDate = (val = "") => {
  if (val === "") {
    return false;
  }
  const MONTHS = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sept",
    "Oct",
    "Nov",
    "Dec",
  ];

  const date = new Date(val);

  const dateString = `${date.getDate()} ${
    MONTHS[date.getMonth()]
  }, ${date.getFullYear()}`;

  return dateString;
};

/**
 * Async function to pause execution for specific time
 * @param {number} ms
 * @returns {Promise}
 */
export function wait(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * Async function to keep re-trying fetching api until successful
 * @param {function} fetchApi
 * @param {function} onFail
 * @param {number} maxTry
 * @returns {[Promise, function]}
 */
export function fetchRetry(fetchApi, onFail = null, maxTryCount = null) {
  let maxTry = maxTryCount;
  async function retry(n = 1) {
    try {
      if (maxTry === -1) return null;
      const result = await fetchApi();
      return result;
    } catch (e) {
      if (onFail) {
        onFail(e);
      }
      console.error(e);

      if (!maxTry || n < maxTry) {
        await wait(Math.floor(Math.exp(n) * 1000));
        return retry(n + 1);
      }
      throw new Error(e);
    }
  }
  function cancelRetry() {
    maxTry = -1;
  }
  return [retry(), cancelRetry];
}

// we'll cache user data because iOS is sending it as query params which gets lost
// if the user moves from the landing URL to another.
const userDataCached = {};
/**
 *
 * @returns {object} user and app details
 */
export function getUserData() {
  let userAuth = null;
  const searchParams = new URLSearchParams(window.location.search);
  let language = getSafeSearchParam("language");
  let appVersion = getSafeSearchParam("app-version");
  let sessionId = getSafeSearchParam("userSessionId");
  const platform = getOsName(navigator.userAgent);
  const urlToken = searchParams.get("auth") || "";
  if (urlToken) {
    // URLSearchParams().get() removes '+' and replaces it with whitespace thus invalidating the token
    userAuth = urlToken.split(" ").join("+");
  } else {
    appVersion = getAppData("appVersion") || null;
    language = getAppData("userLang") || null;
    sessionId = getAppData("userSessionId") || null;
    userAuth = getUserAuth();
  }
  userDataCached.language = language || userDataCached.language;
  userDataCached.appVersion = appVersion || userDataCached.appVersion;
  userDataCached.userAuth = userAuth || userDataCached.userAuth;
  userDataCached.sessionId = sessionId || userDataCached.sessionId;
  let authToken =
    userAuth || userDataCached.userAuth || DEFAULT_AUTH_TOKEN || "";
  authToken =
    typeof authToken === "string" ? authToken : JSON.stringify(authToken);
  authToken = authToken.replace(/(\r\n|\n|\r)/gm, "");
  return {
    language: userDataCached.language,
    appVersion: userDataCached.appVersion,
    platform,
    userAuth: authToken,
    sessionId: userDataCached.sessionId,
  };
}

/**
 * @method getAppReferrer - referrer property is sent via query params, this may be used e.g. instrumentation
 */
let cachedReferrer = "";
export function getAppReferrer() {
  cachedReferrer = getSafeSearchParam("referrer") || cachedReferrer;
  return cachedReferrer;
}

/**
 *Function to report error to gcp stackdriver
 * @param {error} error
 *
 */

export const reportError = (error) => {
  return;
  //   if (!isBrowser()) return;
  //   return;
  //   const params = { stackframes: ErrorStackParser.parse(error) };
  //   fetch(`${window.location.origin}/report-browser-error`, {
  //     method: "POST",
  //     body: JSON.stringify({
  //       ...params,
  //       actualError: `${error}`,
  //       userAgent: nUserAgent(),
  //       url: window.location.href,
  //       appVersion: MOJ_APP_VERSION,
  //     }),
  //     headers: {
  //       Accept: "application/json",
  //       "Content-Type": "application/json",
  //     },
  //   }).catch(noop);
};

/**
 *
 * @param {String} s
 * @param {Boolean} isDesktopSize
 * @returns {String}
 */
export const getSanityImgUrl = (s, isDesktopSize) =>
  s && s.includes("cdn.sanity")
    ? `${s}?auto=format${isDesktopSize ? "" : "&w=720&q=70"}`
    : s;

/**
 * @function formatNumbersKMB
 * @param {Number} num
 * @returns {String}
 */
export const formatNumbersKMB = (num) => {
  let numi = num;
  if (typeof num === typeof String()) {
    numi = parseInt(num);
  }
  if (Number.isNaN(numi)) numi = 0;
  if (numi >= 10 ** 6)
    return numi % 10 === 0
      ? `${(numi / 10 ** 6).toFixed(0)}M`
      : `${(numi / 10 ** 6).toFixed(1)}M`;
  if (numi >= 10 ** 3)
    return numi % 10 === 0
      ? `${(numi / 10 ** 3).toFixed(0)}K`
      : `${(numi / 10 ** 3).toFixed(1)}K`;
  return num;
};

export function isTestEnv() {
  return process.env.NODE_ENV === "test";
}
/**
 *
 * @param {Object} s
 * @returns {Number}
 */
export const getSanityImgAspectRatio = (s) =>
  s ? 100 / s.dimensions.aspectRatio : s;

class NativeActionManager {
  constructor() {
    this.pendingActions = [];
    this.checkerId =
      process.env.NODE_ENV !== "test" &&
      setInterval(() => {
        if (NativeActionManager.hasNativeBridge) {
          this.pendingActions.forEach((action) => this.postActionToApp(action));
          this.pendingActions = [];
          clearInterval(this.checkerId);
        }
      }, 100);
  }

  static get hasNativeBridge() {
    if (isBrowser()) {
      return (
        window?.Android?.onAction ||
        window.webkit?.messageHandlers?.mojApp?.postMessage
      );
    }
    return false;
  }

  /**
   * @method _postActionToApp - post action to native apps (iOS/Android)
   * @param {Object} payload
   */
  postActionToApp(payload) {
    if (!NativeActionManager.hasNativeBridge) {
      this.pendingActions.push(payload);
      return;
    }
    if (isAndroid()) {
      window.Android?.onAction?.(payload);
    } else if (isIos()) {
      window.webkit?.messageHandlers?.mojApp?.postMessage(payload);
    }
  }
}
const nativeActionManager = new NativeActionManager();
export const postActionToApp =
  nativeActionManager.postActionToApp.bind(nativeActionManager);

export const linksOnSideBar = [
  {
    n: "Privacy Policy",
    l: "policies/privacy-policy",
  },
  { n: "Terms & Conditions", l: "policies/terms" },
  {
    n: "Community Guidelines",
    l: "policies/content-policy",
  },
  {
    n: "Referral Terms",
    l: "policies/referral-terms",
  },
  { n: "Referral FAQ", l: "policies/referral-faq" },
  { n: "Contact Us", l: "contact" },
];

export const redirectSidebarLinks = [{ n: "Wallet", l: "wallet", new: true }];

/**
 * Get app name
 * @method getAppName
 */
let cachedAppName = "";
export const getAppName = () => {
  if (isAndroid()) {
    cachedAppName =
      (window.Android && window.Android.get("appName")) || cachedAppName;
  } else if (isIos()) {
    cachedAppName = getSafeSearchParam("app") || cachedAppName;
  }
  return cachedAppName;
};

export const parseCookie = (str) =>
  str
    ?.split(";")
    .map((v) => v.split("="))
    .reduce((acc, v) => {
      acc[decodeURIComponent(v[0].trim())] = decodeURIComponent(v[1].trim());
      return acc;
    }, {});

/**
 * @method debounce
 * @param {object} fn - Function which should be debounced
 * @param {number} time - debounce time
 * @returns {object}
 */
export const debounce = (fn, time) => {
  let id = null;
  return function debouncedFn(...args) {
    clearTimeout(id);
    id = setTimeout(() => fn(...args), time);
  };
};
export const roundNumTo = (num, to = 0) =>
  Math.round(num / 10 ** to) * 10 ** to;

const getWebkitAsyncData = async (paramName) => {
  if (typeof window !== "undefined") {
    try {
      await window.webkit?.messageHandlers?.onAction?.postMessage({
        jsonObject: paramName,
      });
      const info = window.getInformation();
      return typeof info === "string"
        ? JSON.parse(info)?.[paramName]
        : info[paramName];
    } catch (e) {
      console.log(e);
    }
  } else {
    throw new Error("Window is undefined. Please call it from browser");
  }
};

export const getAppVersion = async () => {
  if (window.Android) return window.Android.get("appVersion");
  if (window.webkit) {
    const data = await getWebkitAsyncData("appVersion");
    return data;
  }
  return 4884;
};

export const openRedirectAction = (action) => {
  if (window.Android) {
    window.Android.onAction(JSON.stringify(action));
  } else if (window.webkit) {
    window.webkit.messageHandlers.mojApp.postMessage({
      jsonObject: JSON.stringify(action),
    });
  } else {
    console.log(action);
  }
};

export const openAction = (action) => {
  if (window.Android) {
    const { base64image, shareText } = action;
    window.Android.shareImage(base64image, shareText);
  } else if (window.webkit) {
    window.webkit.messageHandlers.onAction.postMessage({
      jsonObject: JSON.stringify(action),
    });
  } else {
    console.log(action);
  }
};

export const getWhatsappURL = (str) => {
  const whatsappShareUrl = "https://api.whatsapp.com/send?text=";
  return whatsappShareUrl + window.encodeURIComponent(str);
};

export const toggleLikeCountVideoFeed = (list, action, index) => {
  return list.map((item, idx) => {
    if (idx === index) {
      return {
        ...item,
        lc: parseInt(item?.lc) + (action ? 1 : -1),
        pl: action ? 1 : 0,
      };
    }
    return item;
  });
};
